import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { TicketFields } from '../../types';
import { chatService, ChatSlotResponse, ChatSystemMessage } from '../../services/chatService';
import { useTranslation } from 'react-i18next';
import { evidenceService, EvidenceCategory } from '../../services/evidence.service';
import EvidenceUploadButton from './evidence/EvidenceUploadButton';
import EvidenceCard, { EvidenceStatus } from './evidence/EvidenceCard';
import VerticalStepperWithAI from '../chat/VerticalStepperWithAI';
import { StepItem } from '../chat/types';
import LoadingSpinner from '../common/LoadingSpinner';
import { getAIRecsFlag } from '../../config/featureFlags';
import { recommendationService, RecommendationPayload } from '../../services/recommendation.service';
import { sessionService } from '../../services/session.service';
import { debugError, debugInfo, debugWarn } from '../../utils/debug';

export interface GuidedEvidenceFormProps {
  ticketId: string;
  extractedFields: TicketFields;
  onComplete?: () => void;
  canUploadEvidence?: boolean;
}

type GuidedChatMessage = {
  role: 'system' | 'user';
  content: string;
  helperText?: string | null;
  stepKey?: string | null;
  slotKey?: string | null;
};

type EvidenceItem = {
  id: string;
  category: EvidenceCategory;
  filename: string;
  status: EvidenceStatus;
};

type EvidenceConfirmState = {
  evidenceId: string;
  category: EvidenceCategory;
  prompt: string;
  pendingCorrection: boolean;
  stepKey: string;
};

const GuidedEvidenceForm: React.FC<GuidedEvidenceFormProps> = ({
  ticketId,
  extractedFields,
  onComplete,
  canUploadEvidence = true,
}) => {
  const { i18n, t } = useTranslation(['tickets', 'common']);
  const [messages, setMessages] = useState<GuidedChatMessage[]>([]);
  const [currentStep, setCurrentStep] = useState<ChatSlotResponse | null>(null);
  const [textInput, setTextInput] = useState('');
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [evidenceEnabled, setEvidenceEnabled] = useState(false);
  const [evidenceItems, setEvidenceItems] = useState<EvidenceItem[]>([]);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [chatDone, setChatDone] = useState(false);
  const completionGuardRef = useRef(false);
  const [evidenceConfirm, setEvidenceConfirm] = useState<EvidenceConfirmState | null>(null);
  const [correctionText, setCorrectionText] = useState('');
  const [evidenceConfirmed, setEvidenceConfirmed] = useState(false);
  const maxFreeTextLength = 100;
  const maxAddDetailsLength = 500;
  const [recommendationsByStep, setRecommendationsByStep] = useState<Record<string, RecommendationPayload>>({});
  const [dismissedRecommendations, setDismissedRecommendations] = useState<Set<string>>(new Set());
  const [recommendationStatusByStep, setRecommendationStatusByStep] = useState<
    Record<string, { status: 'idle' | 'loading' | 'success' | 'error'; error?: string | null }>
  >({});
  const loggedRecommendationIdsRef = useRef<Set<string>>(new Set());
  const [needsReviewSteps, setNeedsReviewSteps] = useState<Set<string>>(new Set());
  const [detailAttemptedSteps, setDetailAttemptedSteps] = useState<Set<string>>(new Set());
  const [addDetailsUsedByStepKey, setAddDetailsUsedByStepKey] = useState<Record<string, boolean>>({});
  const refreshedStepKeysRef = useRef<Set<string>>(new Set());
  const [lastCompletedStepKey, setLastCompletedStepKey] = useState<string | null>(null);
  const [lastRefreshStatus, setLastRefreshStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [lastRefreshError, setLastRefreshError] = useState<string | null>(null);
  const aiRecsFlag = getAIRecsFlag();
  const aiRecsEnabled = aiRecsFlag.enabled;
  const [pendingNextStep, setPendingNextStep] = useState<ChatSlotResponse | null>(null);
  const [pendingCompletion, setPendingCompletion] = useState<{ nextEvidenceEnabled: boolean } | null>(null);
  const [pendingCompletedStepKey, setPendingCompletedStepKey] = useState<string | null>(null);
  const [awaitingRecommendationDecision, setAwaitingRecommendationDecision] = useState(false);
  const [rollbackUsed, setRollbackUsed] = useState(false);
  const [appealPath, setAppealPath] = useState<'NOT_AT_FAULT' | 'AT_FAULT' | null>(null);
  const [pathRedirectUsed, setPathRedirectUsed] = useState(false);
  const [mitigationReasons, setMitigationReasons] = useState<string[]>([]);
  const [mitigationNotesByReason, setMitigationNotesByReason] = useState<Record<string, string>>({});
  const [savingMitigation, setSavingMitigation] = useState(false);
  const [weatherDetails, setWeatherDetails] = useState<{
    label: string;
    tempC: number | null;
    precipitationMm: number | null;
    windKph: number | null;
    observationTime: string | null;
  } | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherReason, setWeatherReason] = useState<string | null>(null);
  const [customReasonInput, setCustomReasonInput] = useState('');
  const [sessionSlotValues, setSessionSlotValues] = useState<Record<string, any>>({});
  const [editingStepKey, setEditingStepKey] = useState<string | null>(null);
  const [editingMode, setEditingMode] = useState<'ADD_DETAILS' | null>(null);
  const [editingRequiredInfo, setEditingRequiredInfo] = useState<string[]>([]);
  const [addDetailsText, setAddDetailsText] = useState('');
  const [addingDetails, setAddingDetails] = useState(false);
  const [ticketMeta, setTicketMeta] = useState({
    plateNumber: extractedFields.plateNumber,
    ticketNumber: extractedFields.ticketNumber,
    violationDateTime: extractedFields.violationDateTime,
    issuingLocation: extractedFields.issuingLocation,
  });
  const [editingTicketMeta, setEditingTicketMeta] = useState(false);
  const [ticketMetaDraft, setTicketMetaDraft] = useState({
    plateNumber: extractedFields.plateNumber || '',
    ticketNumber: extractedFields.ticketNumber || '',
    violationDateTime: extractedFields.violationDateTime || '',
    issuingLocation: extractedFields.issuingLocation || '',
  });
  const [savingTicketMeta, setSavingTicketMeta] = useState(false);

  const quickReplies: string[] = currentStep?.systemMessage?.quickReplies ?? [];
  const options: { value: string; label: string }[] = currentStep?.options ?? [];

  const formatDateTimeLocal = (value: string | null) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    const pad = (num: number) => `${num}`.padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(
      parsed.getHours()
    )}:${pad(parsed.getMinutes())}`;
  };


  const applyTicketMetaDraft = async () => {
    setSavingTicketMeta(true);
    try {
      const payload = {
        ...extractedFields,
        plateNumber: ticketMetaDraft.plateNumber || null,
        ticketNumber: ticketMetaDraft.ticketNumber || null,
        violationDateTime: ticketMetaDraft.violationDateTime
          ? new Date(ticketMetaDraft.violationDateTime).toISOString()
          : null,
        issuingLocation: ticketMetaDraft.issuingLocation || null,
      };
      const response = await sessionService.updateTicketFields(ticketId, payload);
      if (!response?.success) {
        throw new Error(response?.message || 'Unable to update ticket details.');
      }
      setTicketMeta({
        plateNumber: payload.plateNumber,
        ticketNumber: payload.ticketNumber,
        violationDateTime: payload.violationDateTime,
        issuingLocation: payload.issuingLocation,
      });
      setEditingTicketMeta(false);
      toast.success(t('tickets:upload.toast.success'));
    } catch (error: any) {
      toast.error(error.message || 'Unable to update ticket details.');
    } finally {
      setSavingTicketMeta(false);
    }
  };

  useEffect(() => {
    let active = true;
    const bootstrap = async () => {
      setLoading(true);
      try {
        const history = await chatService.getMessages(ticketId, 200);
        if (active && history.length > 0) {
          setMessages(
            history.map((item: any) => ({
              role: item.role,
              content: item.content,
              helperText: item.helperText || null,
              stepKey: item.stepKey || null,
            }))
          );
        }
        const response = await chatService.bootstrap(ticketId, { language: i18n.language });
        if (!active) return;
        if (response.done) {
          onComplete?.();
          return;
        }
        if (response.systemMessage?.content) {
          setMessages((prev) => {
            const systemMessage = response.systemMessage;
            if (!systemMessage) return prev;
            if (prev.length > 0 && prev[prev.length - 1]?.content === systemMessage.content) {
              return prev;
            }
            return [
              ...prev,
              {
                role: 'system',
                content: systemMessage.content,
                helperText: systemMessage.helperText,
                stepKey: response.stepKey || response.slotKey || null,
              },
            ];
          });
        }
        setCurrentStep(response);
      } catch (error: any) {
        toast.error(error.message || '加载失败，请重试');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    bootstrap();
    return () => {
      active = false;
    };
  }, [ticketId, onComplete]);

  useEffect(() => {
    let active = true;
    const loadSessionMeta = async () => {
      try {
        const sessionResponse = await sessionService.getAppealSession(ticketId);
        const sessionData = sessionResponse?.data || sessionResponse;
        if (!active) return;
        setAppealPath(sessionData?.appealPath || null);
        setPathRedirectUsed(sessionData?.pathRedirectUsed === true);
        const appealContext = sessionData?.appealContext || {};
        setMitigationReasons(Array.isArray(appealContext.mitigationReasons) ? appealContext.mitigationReasons : []);
        setMitigationNotesByReason(appealContext.mitigationNotesByReason || {});
        setSessionSlotValues(sessionData?.slotValues || {});
        const cachedWeather = sessionData?.contextAtTime?.weather?.summary;
        const sessionLocationText = sessionData?.ticket?.locationText || null;
        const sessionViolationTime = sessionData?.ticket?.violationDateTime || null;
        if (cachedWeather) {
          setWeatherReason(null);
          setWeatherDetails({
            label: cachedWeather.weatherLabel,
            tempC: cachedWeather.tempC ?? null,
            precipitationMm: cachedWeather.precipitationMm ?? null,
            windKph: cachedWeather.windKph ?? null,
            observationTime: cachedWeather.observationTime ?? null,
          });
        } else {
          if (!sessionLocationText || !sessionViolationTime) {
            setWeatherReason('MISSING_LOCATION_OR_TIME');
          } else {
            setWeatherLoading(true);
            const weatherResponse = await sessionService.refreshWeatherContext(ticketId);
            if (!active) return;
            const summary = weatherResponse?.weather?.summary;
            if (summary) {
              setWeatherReason(null);
              setWeatherDetails({
                label: summary.weatherLabel,
                tempC: summary.tempC ?? null,
                precipitationMm: summary.precipitationMm ?? null,
                windKph: summary.windKph ?? null,
                observationTime: summary.observationTime ?? null,
              });
            } else {
              setWeatherReason(weatherResponse?.reason || null);
            }
            setWeatherLoading(false);
          }
        }
      } catch (error) {
        debugWarn('[ai-recs] failed to load session metadata', {
          message: (error as any)?.message || 'unknown',
        });
      }
    };
    loadSessionMeta();
    return () => {
      active = false;
    };
  }, [ticketId]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      debugInfo('[recommendations] feature flag', {
        enabled: aiRecsEnabled,
        raw: aiRecsFlag.raw,
        source: aiRecsFlag.source,
      });
      if (!aiRecsFlag.raw) {
        debugWarn(
          '[recommendations] REACT_APP_ENABLE_AI_RECOMMENDATIONS is unset. Add it to frontend/.env and restart yarn start.'
        );
      }
    }
  }, [aiRecsEnabled, aiRecsFlag.source]);

  useEffect(() => {
    if (!currentStep?.slotKey) return;
    if (selectedOption) return;
    const storedValue = sessionSlotValues[currentStep.slotKey];
    if (storedValue) {
      setSelectedOption(String(storedValue));
    }
  }, [currentStep?.slotKey, selectedOption, sessionSlotValues]);

  useEffect(() => {
    if (appealPath !== 'AT_FAULT') return;
    if (currentStep?.slotKey !== 'narrative') return;
    const baseNarrative = String(sessionSlotValues?.narrative || '').trim();
    if (!baseNarrative) return;
    setCustomReasonInput((prev) => prev || baseNarrative);
  }, [appealPath, currentStep?.slotKey, sessionSlotValues]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    Object.entries(recommendationsByStep).forEach(([stepKey, rec]) => {
      if (!rec?.id || loggedRecommendationIdsRef.current.has(rec.id)) return;
      loggedRecommendationIdsRef.current.add(rec.id);
      debugWarn('[ai-recs][ui] rendered review card', {
        stepKey,
        fit: rec.fit || null,
        action: rec.action,
      });
    });
  }, [recommendationsByStep]);

  const resetInput = () => {
    setTextInput('');
    setSelectedOption(null);
  };

  const shouldRunAiReview = (step?: ChatSlotResponse | null) => {
    const type = step?.inputType;
    const allowTypes = new Set(['short_text', 'long_text']);
    const skipTypes = new Set([
      'yes_no',
      'single_select',
      'multi_select',
      'date',
      'time',
      'number',
    ]);
    if (!type) return false;
    if (skipTypes.has(type)) return false;
    return allowTypes.has(type);
  };

  const sanitizeFreeText = (value: string) => {
    let text = value;
    text = text.replace(/https?:\/\/\S+/gi, '');
    text = text.replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '');
    text = text.replace(/\+?\d[\d\s().-]{7,}\d/g, '');
    text = text.replace(/<[^>]*>/g, '');
    text = text.replace(/[*_`~>#]/g, '');
    text = text.replace(/[!?.]{4,}/g, (match) => match.slice(0, 3));
    text = text.replace(/[ \t]{2,}/g, ' ');
    text = text.replace(/\n{3,}/g, '\n\n');
    return text.trim().slice(0, maxFreeTextLength);
  };

  const triggerRecommendation = async (
    stepKey?: string | null,
    stepType?: string,
    inputType?: string
  ): Promise<RecommendationPayload | null> => {
    if (!aiRecsEnabled || !stepKey) return null;
    if (addDetailsUsedByStepKey[stepKey]) {
      debugWarn('[ai-recs] skip refresh: add-details already used', { stepKey });
      return null;
    }
    if (refreshedStepKeysRef.current.has(stepKey)) {
      debugWarn('[ai-recs] skip refresh: already refreshed', { stepKey });
      return null;
    }
    try {
      debugInfo('[recommendations] triggering refresh', { stepKey, stepType });
      setRecommendationStatusByStep((prev) => ({
        ...prev,
        [stepKey]: { status: 'loading', error: null },
      }));
      setLastRefreshStatus('loading');
      setLastRefreshError(null);
      const response = await recommendationService.refresh(ticketId, {
        stepKey,
        stepType,
        inputType,
      });
      const recommendation = response?.recommendation;
      if (recommendation?.id) {
        refreshedStepKeysRef.current.add(stepKey);
        debugInfo('[recommendations] refresh success', {
          stepKey,
          fit: recommendation.fit || null,
        });
        setRecommendationsByStep((prev) => ({
          ...prev,
          [stepKey]: recommendation,
        }));
        if (recommendation.addDetailsUsed) {
          setAddDetailsUsedByStepKey((prev) => ({
            ...prev,
            [stepKey]: true,
          }));
          setDetailAttemptedSteps((prev) => new Set(prev).add(stepKey));
        }
        if (recommendation.skipped) {
          setNeedsReviewSteps((prev) => new Set(prev).add(stepKey));
        }
        setRecommendationStatusByStep((prev) => ({
          ...prev,
          [stepKey]: { status: 'success', error: null },
        }));
        setLastRefreshStatus('success');
        return recommendation;
      }
    } catch (error: any) {
      const message = error?.message || 'Recommendation refresh failed';
      debugError('[recommendations] refresh failed', { stepKey, error: message });
      setRecommendationStatusByStep((prev) => ({
        ...prev,
        [stepKey]: { status: 'error', error: message },
      }));
      setLastRefreshStatus('error');
      setLastRefreshError(message);
    }
    return null;
  };

  const appendSystemMessage = (systemMessage?: ChatSystemMessage, stepKey?: string | null) => {
    if (!systemMessage?.content) return;
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === 'system' && last.stepKey === stepKey) {
        return prev;
      }
      return [
        ...prev,
        {
          role: 'system',
          content: systemMessage.content,
          helperText: systemMessage.helperText,
          stepKey: stepKey || null,
        },
      ];
    });
  };

  const hasSuccessfulEvidence = (items: EvidenceItem[]) =>
    items.some((item) => item.status === 'PROCESSED');

  const attemptCompletion = (
    items: EvidenceItem[],
    reason: 'chat' | 'upload',
    evidenceEnabledOverride?: boolean
  ) => {
    if (!onComplete || completionGuardRef.current) return;
    const resolvedEvidenceEnabled =
      typeof evidenceEnabledOverride === 'boolean' ? evidenceEnabledOverride : evidenceEnabled;
    const hasPendingConfirmation = !!evidenceConfirm;
    const evidenceRequired = false;

    if (!resolvedEvidenceEnabled) {
      completionGuardRef.current = true;
      onComplete();
      return;
    }

    if (hasPendingConfirmation) {
      setChatDone(true);
      toast.info(getLocalizedText('Please confirm the evidence to continue.', '请先确认识别结果再继续。'));
      return;
    }

    if (evidenceRequired && !hasSuccessfulEvidence(items)) {
      setChatDone(true);
      toast.info(getLocalizedText('Please upload evidence to continue.', '请先上传证据再继续。'));
      return;
    }

    if (hasSuccessfulEvidence(items) && evidenceConfirmed) {
      completionGuardRef.current = true;
      onComplete();
      return;
    }

    completionGuardRef.current = true;
    onComplete();
  };

  const handleSubmit = async (
    payload: { userMessage?: string; quickReplyValue?: string },
    displayValue: string
  ) => {
    if (!currentStep || submitting) return;
    setSubmitting(true);
    const isEditingSubmit =
      editingMode === 'ADD_DETAILS' && editingStepKey && currentStep.stepKey === editingStepKey;
    try {
      if (isEditingSubmit) {
        debugWarn('[ai-recs][ui] resubmit start', { stepKey: editingStepKey, ts: Date.now() });
      }
      debugWarn('[chat/next][req]', {
        stepKey: currentStep.stepKey,
        slotKey: currentStep.slotKey,
        inputType: currentStep.inputType,
        payload,
      });
      let nextEvidenceEnabled = evidenceEnabled;
      if (currentStep.slotKey === 'evidenceAvailable') {
        const answer = (payload.quickReplyValue || payload.userMessage || '').toString().toUpperCase();
        nextEvidenceEnabled = answer === 'YES';
        setEvidenceEnabled(nextEvidenceEnabled);
      }
      if (currentStep.slotKey === 'faultStance') {
        const stance = (payload.quickReplyValue || payload.userMessage || '').toString().toUpperCase();
        const resolvedPath = stance === 'AT_FAULT' ? 'AT_FAULT' : 'NOT_AT_FAULT';
        setAppealPath(resolvedPath);
      }
      const response = await chatService.next(ticketId, { ...payload, language: i18n.language });
      const completedStepKey = response.completedStepKey || null;
      debugInfo('[chat/next] completedStepKey', {
        completedStepKey,
        nextStepKey: response.stepKey,
      });
      debugInfo('[ai-recs] flag', { enabled: aiRecsEnabled, source: aiRecsFlag.source });
      const sameStepReturned =
        response.stepKey &&
        currentStep.stepKey &&
        response.stepKey === currentStep.stepKey;
      const missingRequired =
        sameStepReturned &&
        response.systemMessage?.content?.toLowerCase().includes('missing required answer');

      if (missingRequired || (sameStepReturned && !response.completedStepKey)) {
        setCurrentStep(response);
        setSubmitting(false);
        return;
      }

      setMessages((prev) => [...prev, { role: 'user', content: displayValue }]);
      setLastCompletedStepKey(completedStepKey);
      if (isEditingSubmit) {
        setEditingStepKey(null);
        setEditingMode(null);
        setEditingRequiredInfo([]);
        setAddDetailsText('');
        setAwaitingRecommendationDecision(false);
        setPendingCompletedStepKey(null);
      }
      const addDetailsUsed =
        completedStepKey && addDetailsUsedByStepKey[completedStepKey];
      const shouldRunReview = shouldRunAiReview(currentStep);
      if (!shouldRunReview) {
        debugWarn('[ai-recs] skipped (inputType)', {
          stepKey: completedStepKey,
          inputType: currentStep?.inputType || null,
        });
      }
      if (addDetailsUsed) {
        debugWarn('[ai-recs] skip refresh: add-details already used', { stepKey: completedStepKey });
      }
      const recommendation =
        aiRecsEnabled && completedStepKey && !addDetailsUsed && shouldRunReview
          ? await triggerRecommendation(completedStepKey, 'SLOT', currentStep?.inputType)
          : null;

      const detailAttempted =
        completedStepKey && detailAttemptedSteps.has(completedStepKey);
      const shouldPauseForReview =
        recommendation &&
        recommendation.mode === 'NOT_AT_FAULT' &&
        (recommendation.action === 'ASK_DETAILS' || recommendation.action === 'OFFER_REDIRECT') &&
        !detailAttempted;

      if (shouldPauseForReview) {
        debugWarn('[ai-recs][ui] pause for decision', {
          stepKey: completedStepKey,
          nextStepKey: response.stepKey,
          action: recommendation.action,
        });
        setPendingCompletedStepKey(completedStepKey);
        setAwaitingRecommendationDecision(true);
        if (response.done) {
          setPendingCompletion({ nextEvidenceEnabled });
          setCurrentStep(null);
        } else {
          setPendingNextStep(response);
          setCurrentStep(null);
        }
        resetInput();
        return;
      }

      if (response.done) {
        setCurrentStep(null);
        attemptCompletion(evidenceItems, 'chat', nextEvidenceEnabled);
        return;
      }
      appendSystemMessage(response.systemMessage, response.stepKey || response.slotKey || null);
      setCurrentStep(response);
      resetInput();
      if (isEditingSubmit) {
        debugWarn('[ai-recs][ui] resubmit success', { stepKey: editingStepKey, ts: Date.now() });
      }
    } catch (error: any) {
      toast.error(error.message || '提交失败，请重试');
      if (isEditingSubmit) {
        debugWarn('[ai-recs][ui] resubmit error', {
          stepKey: editingStepKey,
          message: error?.message || 'unknown',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getLocalizedText = (en: string, zh: string) => (i18n.language === 'zh' ? zh : en);

  const mitigationReasonOptions = [
    {
      key: 'SHORT_STOP',
      label: getLocalizedText('Delivery / passenger drop-off', '临时上下客或送货'),
    },
    {
      key: 'WEATHER',
      label: getLocalizedText('Poor visibility / severe conditions', '恶劣天气或能见度差'),
    },
    {
      key: 'PAYMENT_ISSUE',
      label: getLocalizedText('App or machine error', '支付系统或机器故障'),
    },
    {
      key: 'EMERGENCY',
      label: getLocalizedText('Vehicle or personal emergency', '车辆或个人紧急情况'),
    },
    {
      key: 'GOOD_FAITH',
      label: getLocalizedText('First offence / short duration / clean record', '初犯或短暂停靠/良好记录'),
    },
  ];

  const buildNarrativeSummary = ({
    selectedTags,
    customReason,
  }: {
    selectedTags: string[];
    customReason: string;
  }) => {
    const normalizedCustom = sanitizeFreeText(customReason || '');
    if (selectedTags.length === 0) return normalizedCustom;
    const tagsLine = `Tags: ${selectedTags.join(', ')}`;
    return normalizedCustom ? `${normalizedCustom}\n\n${tagsLine}` : tagsLine;
  };

  const handleEvidenceUpload = async (category: EvidenceCategory, file: File) => {
    if (evidenceUploading) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(getLocalizedText('Unsupported file type.', '不支持的文件类型'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(getLocalizedText('File size exceeds 10MB limit.', '文件大小超过 10MB 限制'));
      return;
    }

    const tempId = `${Date.now()}-${file.name}`;
    let resolvedId = tempId;
    setEvidenceUploading(true);
    setEvidenceConfirmed(false);
    setEvidenceConfirm(null);
    setCorrectionText('');
    setEvidenceItems((prev) => [
      ...prev,
      { id: tempId, category, filename: file.name, status: 'UPLOADING' },
    ]);

    try {
      const init = await evidenceService.initUpload(ticketId, {
        category,
        contentType: file.type,
        sizeBytes: file.size,
      });
      const evidenceId = init.evidenceId || tempId;
      resolvedId = evidenceId;

      setEvidenceItems((prev) =>
        prev.map((item) =>
          item.id === tempId ? { ...item, id: evidenceId, status: 'PROCESSING' } : item
        )
      );

      const completed = await evidenceService.completeUpload(ticketId, evidenceId, file, {
        language: i18n.language,
      });
      const resolvedStatus: EvidenceStatus =
        completed.status === 'UPLOADED' ||
        completed.status === 'PROCESSING' ||
        completed.status === 'PROCESSED' ||
        completed.status === 'FAILED'
          ? completed.status
          : 'PROCESSED';
      setEvidenceItems((prev) => {
        const next = prev.map((item) =>
          item.id === evidenceId ? { ...item, status: resolvedStatus } : item
        );
        if (resolvedStatus === 'PROCESSED' && chatDone) {
          attemptCompletion(next, 'upload');
        }
        return next;
      });

      const confirmPrompt =
        completed.confirmPrompt?.message ||
        (completed.summary
          ? i18n.language === 'zh'
            ? `${completed.summary} 是否正确？`
            : `${completed.summary} Is that correct?`
          : '');
      if (confirmPrompt) {
        appendSystemMessage({ content: confirmPrompt }, completed.confirmPrompt?.stepKey || 'evidence.confirm');
        setEvidenceConfirm({
          evidenceId,
          category,
          prompt: confirmPrompt,
          pendingCorrection: false,
          stepKey: completed.confirmPrompt?.stepKey || 'evidence.confirm',
        });
      }
    } catch (error: any) {
      setEvidenceItems((prev) =>
        prev.map((item) =>
          item.id === tempId || item.id === resolvedId ? { ...item, status: 'FAILED' } : item
        )
      );
      toast.error(error.message || getLocalizedText('Upload failed. Please try again.', '上传失败，请重试'));
    } finally {
      setEvidenceUploading(false);
    }
  };

  const handleConfirmEvidence = async (confirmed: boolean) => {
    if (!evidenceConfirm) return;
    if (!confirmed && !evidenceConfirm.pendingCorrection) {
      setEvidenceConfirm((prev) => (prev ? { ...prev, pendingCorrection: true } : prev));
      return;
    }
    if (!confirmed && !correctionText.trim()) {
      toast.error(getLocalizedText('Please provide your correction.', '请补充更正内容。'));
      return;
    }
    if (!confirmed && correctionText.trim().length > maxFreeTextLength) {
      toast.error(getLocalizedText('Please keep your correction under 100 characters.', '更正内容请控制在 100 字以内。'));
      return;
    }
    try {
      const correction = confirmed ? undefined : sanitizeFreeText(correctionText);
      const confirmLabel = getLocalizedText("Yes, that's correct", '是的，正确');
      const correctionLabel = getLocalizedText('Not correct / needs changes', '不正确，需要更改');
      setMessages((prev) => [
        ...prev,
        { role: 'user' as const, content: confirmed ? confirmLabel : correctionLabel },
        ...(correction ? [{ role: 'user' as const, content: correction }] : []),
      ]);
      await evidenceService.confirmEvidence(ticketId, evidenceConfirm.evidenceId, {
        confirmed,
        correction,
      });
      setEvidenceConfirmed(true);
      setEvidenceConfirm(null);
      setCorrectionText('');
      attemptCompletion(evidenceItems, 'upload');
    } catch (error: any) {
      toast.error(error.message || getLocalizedText('Confirmation failed. Please try again.', '确认失败，请重试。'));
    }
  };

  const handlePrimarySubmit = () => {
    if (!currentStep) return;
    if (currentStep.inputType === 'short_text' || currentStep.inputType === 'long_text') {
      const isNarrativeWithTags = currentStep.slotKey === 'narrative' && appealPath === 'AT_FAULT';
      if (isNarrativeWithTags) {
        const summary = buildNarrativeSummary({
          selectedTags: mitigationReasons,
          customReason: customReasonInput,
        });
        if (!summary) {
          toast.error(getLocalizedText('Please select a tag or add a reason.', '请选择标签或填写理由。'));
          return;
        }
        if (summary.length > 300) {
          toast.error(getLocalizedText('Please keep your summary concise.', '请保持总结简洁。'));
          return;
        }
        handleSubmit({ userMessage: summary }, summary);
        return;
      }

      const trimmed = textInput.trim();
      if (!trimmed) {
        toast.error('请填写回答内容');
        return;
      }
      if (trimmed.length > maxFreeTextLength) {
        toast.error(getLocalizedText('Please keep your answer under 100 characters.', '回答请控制在 100 字以内。'));
        return;
      }
      const sanitized = sanitizeFreeText(trimmed);
      if (!sanitized) {
        toast.error('请填写回答内容');
        return;
      }
      handleSubmit({ userMessage: sanitized }, sanitized);
      return;
    }

    if (currentStep.inputType === 'single_select' && selectedOption) {
      const optionLabel =
        options.find((opt: { value: string; label: string }) => opt.value === selectedOption)?.label ||
        selectedOption;
      handleSubmit({ quickReplyValue: selectedOption, userMessage: selectedOption }, optionLabel);
      return;
    }

    toast.error('请选择一个选项');
  };

  const handleSingleSelectChange = (optionValue: string) => {
    if (!currentStep || currentStep.inputType !== 'single_select' || submitting) return;
    const optionLabel =
      options.find((opt: { value: string; label: string }) => opt.value === optionValue)?.label ||
      optionValue;
    setSelectedOption(optionValue);
    // Always submit stable internal keys for deterministic transitions.
    handleSubmit({ quickReplyValue: optionValue, userMessage: optionValue }, optionLabel);
  };

  const resolveYesNoValue = (reply: string, index: number) => {
    const normalized = reply.trim().toLowerCase();
    if (normalized.includes('not sure') || normalized.includes('不确定') || normalized.includes('unsure')) {
      return 'NOT_SURE';
    }
    if (normalized === 'yes' || normalized === '是' || normalized.startsWith('yes')) {
      return 'YES';
    }
    if (normalized === 'no' || normalized === '否' || normalized.startsWith('no')) {
      return 'NO';
    }
    if (index === 0) return 'YES';
    if (index === 1) return 'NO';
    return 'NOT_SURE';
  };

  const renderInput = () => {
    if (!currentStep) return null;

    if (currentStep.inputType === 'short_text') {
      const isNarrativeWithTags = currentStep.slotKey === 'narrative' && appealPath === 'AT_FAULT';
      return (
        <div className="space-y-3">
          <input
            type="text"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 shadow-sm transition-all focus:border-emerald-500 focus:ring-emerald-500"
            value={isNarrativeWithTags ? customReasonInput : textInput}
            onChange={(e) =>
              isNarrativeWithTags ? setCustomReasonInput(e.target.value) : setTextInput(e.target.value)
            }
            maxLength={maxFreeTextLength}
            placeholder={getLocalizedText('Enter a brief response', '请输入简要说明')}
          />
          {isNarrativeWithTags && (
            <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600">
              <p className="font-semibold text-slate-700">
                {getLocalizedText('Derived summary preview', '衍生总结预览')}
              </p>
              <p className="mt-1 whitespace-pre-wrap">
                {buildNarrativeSummary({
                  selectedTags: mitigationReasons,
                  customReason: customReasonInput,
                }) || getLocalizedText('Select tags or type your reason.', '请选择标签或填写理由。')}
              </p>
            </div>
          )}
        </div>
      );
    }

    if (currentStep.inputType === 'long_text') {
      const isNarrativeWithTags = currentStep.slotKey === 'narrative' && appealPath === 'AT_FAULT';
      return (
        <div className="space-y-3">
          <textarea
            className="w-full min-h-[140px] rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 shadow-sm transition-all focus:border-emerald-500 focus:ring-emerald-500"
            rows={4}
            value={isNarrativeWithTags ? customReasonInput : textInput}
            onChange={(e) =>
              isNarrativeWithTags ? setCustomReasonInput(e.target.value) : setTextInput(e.target.value)
            }
            maxLength={maxFreeTextLength}
            placeholder={getLocalizedText(
              'I parked for just 5 minutes to drop off a package...',
              '例如：我只是临时停靠 5 分钟送货。'
            )}
          />
          {isNarrativeWithTags && (
            <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600">
              <p className="font-semibold text-slate-700">
                {getLocalizedText('Derived summary preview', '衍生总结预览')}
              </p>
              <p className="mt-1 whitespace-pre-wrap">
                {buildNarrativeSummary({
                  selectedTags: mitigationReasons,
                  customReason: customReasonInput,
                }) || getLocalizedText('Select tags or type your reason.', '请选择标签或填写理由。')}
              </p>
            </div>
          )}
        </div>
      );
    }

    if (currentStep.inputType === 'yes_no' && quickReplies.length > 0) {
      return (
        <div className="flex flex-wrap gap-2">
          {quickReplies.map((reply: string, index: number) => (
            <button
              key={reply}
              type="button"
              onClick={() => {
                const canonicalValue = resolveYesNoValue(reply, index);
                handleSubmit(
                  { quickReplyValue: canonicalValue, userMessage: canonicalValue },
                  reply
                );
              }}
              className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 transition-all hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700"
              disabled={submitting}
            >
              {reply}
            </button>
          ))}
        </div>
      );
    }

    if (currentStep.inputType === 'single_select') {
      return (
        <div className="space-y-2">
          {options.map((opt: { value: string; label: string }) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="radio"
                name={`slot-${currentStep.slotKey}`}
                value={opt.value}
                checked={selectedOption === opt.value}
                onChange={() => handleSingleSelectChange(opt.value)}
                className="text-emerald-600"
              />
              <span className="text-slate-700">{opt.label}</span>
            </label>
          ))}
        </div>
      );
    }

    return null;
  };

  const groupedSteps = (() => {
    const groups: { system?: GuidedChatMessage; responses: GuidedChatMessage[]; key: string; stepKey?: string | null }[] = [];
    const indexByStepKey = new Map<string, number>();

    messages.forEach((message, idx) => {
      const stepKey = message.stepKey || message.slotKey || null;
      const groupKey = stepKey || `msg-${idx}`;
      const existingIndex = stepKey ? indexByStepKey.get(stepKey) : undefined;

      if (message.role === 'system') {
        if (existingIndex !== undefined) {
          groups[existingIndex] = {
            ...groups[existingIndex],
            system: message,
          };
          return;
        }
        groups.push({ system: message, responses: [], key: groupKey, stepKey });
        if (stepKey) indexByStepKey.set(stepKey, groups.length - 1);
        return;
      }

      if (existingIndex !== undefined) {
        groups[existingIndex].responses.push(message);
        return;
      }

      groups.push({ responses: [message], key: groupKey, stepKey });
      if (stepKey) indexByStepKey.set(stepKey, groups.length - 1);
    });

    return groups;
  })();

  const submitLabel = getLocalizedText('Continue', '继续');

  const currentStepContent = currentStep ? (
    <div className="space-y-6">
      {submitting && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <LoadingSpinner size="sm" />
          <span>{getLocalizedText('Preparing the next question...', '正在生成下一步问题...')}</span>
        </div>
      )}
      {(currentStep.slotKey === 'evidenceAvailable' || evidenceEnabled) &&
        !evidenceConfirm &&
        canUploadEvidence &&
        !submitting && (
          <EvidenceUploadButton
            disabled={evidenceUploading}
            onSelect={handleEvidenceUpload}
            labels={{
              payment: getLocalizedText('Upload payment receipt', '上传付款凭证'),
              signage: getLocalizedText('Upload signage photo', '上传标志照片'),
              helper: getLocalizedText(
                'Optional: add a supporting photo to strengthen your appeal.',
                '可选：上传凭证或标志照片以增强申诉力度。'
              ),
            }}
          />
        )}
      {currentStep.slotKey === 'narrative' && appealPath === 'AT_FAULT' && !evidenceConfirm && (
        <div className="space-y-5">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
              {getLocalizedText('Common reasons that may support a reduction', '常见可支持减免的原因')}
            </label>
            <div className="mt-3 flex flex-wrap gap-3">
              {mitigationReasonOptions.map((option) => {
                const selected = mitigationReasons.includes(option.key);
                return (
                  <button
                    key={option.key}
                    type="button"
                    className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all ${
                      selected
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500/20'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-400 hover:bg-emerald-50/40 hover:text-emerald-700'
                    }`}
                    onClick={() => toggleMitigationReason(option.key)}
                    disabled={savingMitigation}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-700">
                {getLocalizedText('Weather at the time of the ticket', '开具罚单时的天气')}
              </div>
              <span className="rounded border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-400">
                {getLocalizedText('Auto-detected', '自动检测')}
              </span>
            </div>
            <div className="mt-2 text-sm text-slate-600">
              {weatherLoading
                ? getLocalizedText('Loading...', '加载中...')
                : weatherDetails
                ? `${weatherDetails.label}, ${weatherDetails.tempC ?? '--'}°C`
                : weatherReason === 'MISSING_LOCATION_OR_TIME'
                ? getLocalizedText(
                    'Weather will appear once ticket details are loaded.',
                    '票据信息加载后将显示天气。'
                  )
                : getLocalizedText('Weather unavailable.', '天气不可用。')}
            </div>
            {weatherDetails && (
              <div className="mt-4 grid grid-cols-2 gap-4 text-xs text-slate-700 md:grid-cols-4">
                <div>
                  <p className="text-slate-400">{getLocalizedText('Condition', '天气状况')}</p>
                  <p className="font-medium">{weatherDetails.label}</p>
                </div>
                <div>
                  <p className="text-slate-400">{getLocalizedText('Temperature', '温度')}</p>
                  <p className="font-medium">{weatherDetails.tempC ?? '--'}°C</p>
                </div>
                <div>
                  <p className="text-slate-400">{getLocalizedText('Wind', '风速')}</p>
                  <p className="font-medium">{weatherDetails.windKph ?? '--'} km/h</p>
                </div>
                <div>
                  <p className="text-slate-400">{getLocalizedText('Precipitation', '降水')}</p>
                  <p className="font-medium">{weatherDetails.precipitationMm ?? '--'} mm</p>
                </div>
              </div>
            )}
            {weatherDetails?.observationTime && (
              <div className="mt-2 text-[11px] text-slate-400">
                {getLocalizedText('Observed', '观测时间')}: {weatherDetails.observationTime}
              </div>
            )}
            {weatherDetails && (
              <div className="mt-1 text-[11px] text-slate-400">
                {getLocalizedText('Data source: OpenWeatherMap', '数据来源：OpenWeatherMap')}
              </div>
            )}
          </div>
        </div>
      )}
      {evidenceConfirm ? (
        <div className="space-y-3">
          {!evidenceConfirm.pendingCorrection ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleConfirmEvidence(true)}
                className="btn-secondary"
              >
                {getLocalizedText("Yes, that's correct", '是的，正确')}
              </button>
              <button
                type="button"
                onClick={() => handleConfirmEvidence(false)}
                className="btn-secondary"
              >
                {getLocalizedText('Not correct / needs changes', '不正确，需要更改')}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="block text-sm text-gray-700">
                {evidenceConfirm.category === 'PAYMENT'
                  ? getLocalizedText(
                      'What amount and time did you pay? (e.g., $4.50 at 2:13 PM)',
                      '你支付了多少金额和时间？（例如：$4.50，2:13 PM）'
                    )
                  : getLocalizedText(
                      'What did the sign say (times/permit/zone)?',
                      '标志上写了什么（时间/许可/区域）？'
                    )}
              </label>
              <input
                type="text"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={correctionText}
                onChange={(e) => setCorrectionText(e.target.value)}
                maxLength={maxFreeTextLength}
                placeholder={getLocalizedText('Enter your correction', '请输入更正内容')}
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => handleConfirmEvidence(false)}
                  className="btn-primary min-w-[140px]"
                >
                  {getLocalizedText('Submit correction', '提交更正')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          {!submitting && renderInput()}
          {!submitting && currentStep.inputType === 'long_text' && (
            <div className="text-xs text-slate-400 text-right">
              {getLocalizedText('Please be descriptive', '尽量描述清楚')}
            </div>
          )}
          {(currentStep.inputType === 'short_text' ||
            currentStep.inputType === 'long_text') && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handlePrimarySubmit}
                disabled={submitting}
                className="flex min-w-[160px] items-center justify-center gap-2 rounded-xl bg-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? getLocalizedText('Submitting...', '提交中...') : submitLabel}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  ) : null;

  const beginAddDetails = async (stepKey: string, recommendation: RecommendationPayload) => {
    debugWarn('[ai-recs][ui] Add details clicked', {
      stepKey,
      completedStepKey: pendingCompletedStepKey,
      recommendation,
      ts: Date.now(),
    });
    await recommendationService.submitDecision(ticketId, {
      stepKey,
      decision: 'ADD_DETAILS_OPENED',
    });
    setEditingStepKey(stepKey);
    setEditingMode('ADD_DETAILS');
    setEditingRequiredInfo(recommendation.bullets || []);
    setAddDetailsText('');
    setAwaitingRecommendationDecision(true);
    requestAnimationFrame(() => {
      const target = document.querySelector(`[data-step-key="${stepKey}"]`);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  const cancelAddDetails = () => {
    setEditingStepKey(null);
    setEditingMode(null);
    setEditingRequiredInfo([]);
    setAddDetailsText('');
  };

  const submitAddDetails = async (stepKey: string) => {
    if (addingDetails) return;
    const value = addDetailsText.trim();
    if (!value) {
      toast.info(getLocalizedText('Please add more details to continue.', '请先补充更多细节。'));
      return;
    }
    setAddingDetails(true);
    setAddDetailsUsedByStepKey((prev) => ({ ...prev, [stepKey]: true }));
    setDetailAttemptedSteps((prev) => new Set(prev).add(stepKey));
    debugWarn('[ai-recs][ui] resubmit start', { stepKey, ts: Date.now() });
    try {
      await recommendationService.submitAddDetails(ticketId, {
        stepKey,
        text: value,
      });
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: value, stepKey },
      ]);
      setEditingStepKey(null);
      setEditingMode(null);
      setEditingRequiredInfo([]);
      setAddDetailsText('');
      const detailAttemptedNow = true;
      resumeAfterDecision(stepKey);
      debugWarn('[ai-recs][ui] resubmit success', { stepKey, ts: Date.now() });
    } catch (error: any) {
      toast.error(error.message || getLocalizedText('Unable to submit details.', '提交细节失败。'));
      debugWarn('[ai-recs][ui] resubmit error', {
        stepKey,
        message: error?.message || 'unknown',
      });
    } finally {
      setAddingDetails(false);
    }
  };

  const saveMitigationContext = async (
    reasons: string[],
    notes: Record<string, string>
  ) => {
    setSavingMitigation(true);
    try {
      const sanitizedNotes: Record<string, string> = {};
      reasons.forEach((reason) => {
        const sanitized = sanitizeFreeText(notes[reason] || '');
        if (sanitized) {
          sanitizedNotes[reason] = sanitized;
        }
      });
      await sessionService.updateMitigationContext(ticketId, {
        reasons,
        notes: sanitizedNotes,
      });
      setMitigationNotesByReason(sanitizedNotes);
    } catch (error: any) {
      toast.error(error.message || getLocalizedText('Unable to save mitigation details.', '无法保存补充信息。'));
    } finally {
      setSavingMitigation(false);
    }
  };

  const toggleMitigationReason = (reasonKey: string) => {
    const nextReasons = mitigationReasons.includes(reasonKey)
      ? mitigationReasons.filter((item) => item !== reasonKey)
      : [...mitigationReasons, reasonKey];
    const nextNotes = { ...mitigationNotesByReason };
    if (!nextReasons.includes(reasonKey)) {
      delete nextNotes[reasonKey];
    }
    setMitigationReasons(nextReasons);
    setMitigationNotesByReason(nextNotes);
    saveMitigationContext(nextReasons, nextNotes);
  };

  const handleMitigationNoteChange = (reasonKey: string, value: string) => {
    setMitigationNotesByReason((prev) => ({
      ...prev,
      [reasonKey]: value.slice(0, maxFreeTextLength),
    }));
  };

  const handleMitigationNoteBlur = (reasonKey: string) => {
    const nextNotes = {
      ...mitigationNotesByReason,
      [reasonKey]: sanitizeFreeText(mitigationNotesByReason[reasonKey] || ''),
    };
    setMitigationNotesByReason(nextNotes);
    saveMitigationContext(mitigationReasons, nextNotes);
  };

  const canAttachCurrent =
    !!currentStepContent && groupedSteps.length > 0 && groupedSteps[groupedSteps.length - 1].responses.length === 0;

  const stepItems: StepItem[] = groupedSteps.map((group, index) => {
    const isCurrent = canAttachCurrent && index === groupedSteps.length - 1;
    const resolvedStepKey = group.stepKey || group.key;
    const itemKey = `${resolvedStepKey}-${index}`;
    const recommendationKey = group.stepKey || resolvedStepKey;
    const recommendation = aiRecsEnabled ? recommendationsByStep[recommendationKey] : null;
    const shouldHideRecommendation =
      !recommendation || dismissedRecommendations.has(recommendation.id);
    const recStatus = recommendationStatusByStep[recommendationKey]?.status || 'idle';
    const recError = recommendationStatusByStep[recommendationKey]?.error || null;
    const isEditingThisStep =
      editingMode === 'ADD_DETAILS' && editingStepKey && group.stepKey === editingStepKey;
    const recommendationActions = recommendation && recommendation.mode !== 'AT_FAULT'
      ? (() => {
          const actionType = recommendation.action;
          const detailsAlreadyAttempted = detailAttemptedSteps.has(recommendationKey) || recommendation.addDetailsUsed;
          if (actionType === 'PROCEED') {
            return { primaryLabel: 'Continue', primaryType: 'CONTINUE' as const };
          }
          if (actionType === 'ASK_DETAILS') {
            if (detailsAlreadyAttempted) {
              return { primaryLabel: 'Continue', primaryType: 'CONTINUE' as const };
            }
            const primaryLabel =
              recommendation.fit === 'MISMATCH' ? 'Edit answer' : 'Add details';
            return {
              primaryLabel,
              primaryType: 'ASK_DETAILS' as const,
              secondaryLabel: 'Continue',
              secondaryType: 'KEEP_ANYWAY' as const,
            };
          }
          return { primaryLabel: 'Continue', primaryType: 'CONTINUE' as const };
        })()
      : undefined;

    return {
      key: itemKey,
      stepKey: resolvedStepKey,
      title: '',
      badgeLabel: getLocalizedText('Previous', '上一题'),
      content: (
        <div className="space-y-3" data-step-key={resolvedStepKey}>
          {needsReviewSteps.has(resolvedStepKey) && (
            <div className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
              {getLocalizedText('Needs review', '需要复核')}
            </div>
          )}
          {group.system && (
            <div className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {getLocalizedText('Previous question', '上一题问题')}
              </div>
              <div className="text-sm text-slate-700">{group.system.content}</div>
              {group.system.helperText && (
                <div className="text-xs text-slate-500">{group.system.helperText}</div>
              )}
            </div>
          )}
          {group.responses.length > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {getLocalizedText('Previous response', '上一题回答')}
              </div>
              <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-800 space-y-1">
                <div className="text-xs font-semibold text-slate-500">
                  {getLocalizedText('Your answer', '你的回答')}
                </div>
                {group.responses.map((response, responseIndex) => (
                  <div key={`${group.key}-resp-${responseIndex}`}>{response.content}</div>
                ))}
              </div>
            </div>
          )}
          {isCurrent && currentStepContent && <div className="pt-2">{currentStepContent}</div>}
          {isEditingThisStep && (
            <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 space-y-2">
              <div className="text-xs font-semibold text-blue-700">
                {getLocalizedText('Add details', '补充细节')}
              </div>
              {editingRequiredInfo.length > 0 && (
                <ul className="list-disc pl-5 text-xs text-blue-700 space-y-1">
                  {editingRequiredInfo.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              <textarea
                className="w-full rounded-md border-blue-200 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                rows={3}
                value={addDetailsText}
                onChange={(event) => setAddDetailsText(event.target.value)}
                maxLength={maxAddDetailsLength}
                placeholder={getLocalizedText('Add more details here', '在此补充更多细节')}
              />
              <div className="flex justify-end gap-2">
                <button type="button" className="btn-secondary" onClick={cancelAddDetails}>
                  {getLocalizedText('Cancel', '取消')}
                </button>
                <button
                  type="button"
                  className="btn-primary min-w-[140px] disabled:opacity-60"
                  onClick={() => submitAddDetails(resolvedStepKey)}
                  disabled={addingDetails}
                >
                  {addingDetails ? getLocalizedText('Saving...', '提交中...') : getLocalizedText('Submit details', '提交细节')}
                </button>
              </div>
            </div>
          )}
          {aiRecsEnabled &&
            process.env.NODE_ENV !== 'production' &&
            group.responses.length > 0 &&
            !recommendation &&
            (recStatus === 'loading' ? (
              <div className="text-xs text-gray-400">Generating recommendation...</div>
            ) : recStatus === 'error' ? (
              <div className="text-xs text-red-500">
                Recommendation failed: {recError || 'Unknown error'}
              </div>
            ) : (
              <div className="text-xs text-gray-400">
                No AI recommendation for stepKey={recommendationKey}
              </div>
            ))}
        </div>
      ),
      recommendation: !shouldHideRecommendation ? recommendation : undefined,
      recommendationActions: !shouldHideRecommendation ? recommendationActions : undefined,
    };
  });

  if (currentStepContent && !canAttachCurrent) {
    stepItems.push({
      key: `${currentStep?.stepKey || currentStep?.slotKey || 'current-step'}-current`,
      title: getLocalizedText('Current question', '当前问题'),
      content: currentStepContent,
    });
  }

  const resumeAfterDecision = (stepKey: string) => {
    if (awaitingRecommendationDecision && pendingCompletedStepKey === stepKey) {
      debugWarn('[ai-recs][ui] resume after decision', {
        stepKey,
        pendingNextStep: pendingNextStep?.stepKey || null,
        pendingCompletion: !!pendingCompletion,
      });
      setAwaitingRecommendationDecision(false);
      setPendingCompletedStepKey(null);
      if (pendingNextStep) {
        appendSystemMessage(pendingNextStep.systemMessage, pendingNextStep.stepKey || pendingNextStep.slotKey || null);
        setCurrentStep(pendingNextStep);
        setPendingNextStep(null);
      } else if (pendingCompletion) {
        attemptCompletion(evidenceItems, 'chat', pendingCompletion.nextEvidenceEnabled);
        setPendingCompletion(null);
      }
    }
  };

  const handlePrimaryAction = async (
    stepKey: string,
    actionType: 'CONTINUE' | 'ASK_DETAILS' | 'KEEP_ANYWAY' | 'REDIRECT'
  ) => {
    if (!aiRecsEnabled) return;
    if (actionType === 'REDIRECT') {
      await handleRedirectAction(stepKey);
      return;
    }
    const recommendation = recommendationsByStep[stepKey];
    if (!recommendation) return;
    try {
      debugWarn('[ai-recs][ui] primary action clicked', {
        stepKey,
        actionType,
        ts: Date.now(),
      });
      if (actionType === 'ASK_DETAILS') {
        await beginAddDetails(stepKey, recommendation);
        return;
      }
      await recommendationService.submitDecision(ticketId, {
        stepKey,
        decision: 'CONTINUE',
      });
      setRecommendationsByStep((prev) => ({
        ...prev,
        [stepKey]: { ...recommendation, skipped: true },
      }));
      if (recommendation.rollback_to_stepKey) {
        if (!rollbackUsed) {
          try {
            await chatService.rollback(ticketId, {
              targetStepKey: recommendation.rollback_to_stepKey,
              needsReviewSlots: [stepKey],
            });
            setRollbackUsed(true);
            const next = await chatService.bootstrap(ticketId, { language: i18n.language });
            setCurrentStep(next);
            setPendingNextStep(null);
            setPendingCompletion(null);
            setAwaitingRecommendationDecision(false);
            setPendingCompletedStepKey(null);
            return;
          } catch (error: any) {
            setNeedsReviewSteps((prev) => new Set(prev).add(stepKey));
          }
        } else {
          setNeedsReviewSteps((prev) => new Set(prev).add(stepKey));
        }
      }
      resumeAfterDecision(stepKey);
    } catch (error: any) {
      toast.error(error.message || getLocalizedText('Unable to save feedback.', '无法保存反馈。'));
    }
  };

  const handleSecondaryAction = async (
    stepKey: string,
    actionType?: 'KEEP_ANYWAY' | 'EDIT_ANSWER'
  ) => {
    if (!aiRecsEnabled) return;
    if (!actionType) return;
    const recommendation = recommendationsByStep[stepKey];
    if (!recommendation) return;
    try {
      debugWarn('[ai-recs][ui] secondary action clicked', {
        stepKey,
        actionType,
        ts: Date.now(),
      });
      await recommendationService.submitDecision(ticketId, {
        stepKey,
        decision: 'CONTINUE',
      });
      setRecommendationsByStep((prev) => ({
        ...prev,
        [stepKey]: { ...recommendation, skipped: true },
      }));
      setNeedsReviewSteps((prev) => new Set(prev).add(stepKey));
      resumeAfterDecision(stepKey);
    } catch (error: any) {
      toast.error(error.message || getLocalizedText('Unable to save feedback.', '无法保存反馈。'));
    }
  };

  const handleRedirectAction = async (stepKey: string) => {
    if (!aiRecsEnabled) return;
    if (pathRedirectUsed) {
      toast.info(getLocalizedText('Redirect already used.', '已使用一次路径切换。'));
      return;
    }
    try {
      debugWarn('[ai-recs][ui] redirect action clicked', { stepKey, ts: Date.now() });
      await sessionService.updateAppealPath(ticketId, { appealPath: 'AT_FAULT' });
      setAppealPath('AT_FAULT');
      setPathRedirectUsed(true);
      resumeAfterDecision(stepKey);
    } catch (error: any) {
      toast.error(error.message || getLocalizedText('Unable to switch path.', '无法切换路径。'));
    }
  };

  const activeStepKey =
    canAttachCurrent && groupedSteps.length > 0
      ? `${(groupedSteps[groupedSteps.length - 1].stepKey || groupedSteps[groupedSteps.length - 1].key)}-${
          groupedSteps.length - 1
        }`
      : currentStep
      ? `${currentStep?.stepKey || currentStep?.slotKey || 'current-step'}-current`
      : undefined;

  const appealPathSummary =
    appealPath === 'AT_FAULT'
      ? getLocalizedText('At fault, requesting leniency', '愿意承认责任，申请减免')
      : appealPath === 'NOT_AT_FAULT'
      ? getLocalizedText('Not at fault, disputing the ticket', '不认可责任，申请撤销')
      : getLocalizedText('Assessment pending', '待确认立场');

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 md:flex-row">
      <aside className="w-full shrink-0 space-y-6 md:w-80 lg:w-96">
        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)]">
          <h3 className="mb-4 text-xs font-bold uppercase tracking-wider text-slate-400">
            {getLocalizedText('Ticket Details', '罚单信息')}
          </h3>
          {!editingTicketMeta ? (
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-emerald-50 p-2 text-emerald-500">🎫</div>
                <div>
                  <p className="text-xs text-slate-400">{t('evidence.meta.ticketNumber', { ns: 'tickets' })}</p>
                  <p className="font-mono font-semibold text-slate-700">
                    {ticketMeta.ticketNumber || t('evidence.meta.unknown', { ns: 'tickets' })}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-emerald-50 p-2 text-emerald-500">🚗</div>
                <div>
                  <p className="text-xs text-slate-400">{t('evidence.meta.plate', { ns: 'tickets' })}</p>
                  <p className="font-mono font-semibold text-slate-700">
                    {ticketMeta.plateNumber || t('evidence.meta.unknown', { ns: 'tickets' })}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-emerald-50 p-2 text-emerald-500">📍</div>
                <div>
                  <p className="text-xs text-slate-400">{t('evidence.meta.location', { ns: 'tickets' })}</p>
                  <p className="text-sm font-medium text-slate-700">
                    {ticketMeta.issuingLocation || t('evidence.meta.unknown', { ns: 'tickets' })}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-emerald-50 p-2 text-emerald-500">⏰</div>
                <div>
                  <p className="text-xs text-slate-400">{t('evidence.meta.violationTime', { ns: 'tickets' })}</p>
                  <p className="text-sm font-medium text-slate-700">
                    {ticketMeta.violationDateTime || t('evidence.meta.unknown', { ns: 'tickets' })}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
                <span className="text-xs text-slate-400">
                  {getLocalizedText('Need to fix this?', '需要修改吗？')}
                </span>
                <button
                  type="button"
                  className="text-xs font-medium text-emerald-600 transition-colors hover:text-emerald-700"
                  onClick={() => {
                    setTicketMetaDraft({
                      plateNumber: ticketMeta.plateNumber || '',
                      ticketNumber: ticketMeta.ticketNumber || '',
                      violationDateTime: formatDateTimeLocal(ticketMeta.violationDateTime),
                      issuingLocation: ticketMeta.issuingLocation || '',
                    });
                    setEditingTicketMeta(true);
                  }}
                >
                  {t('common.edit', { defaultValue: 'Edit Details' })}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs font-semibold text-slate-500">
                  {t('evidence.meta.plate', { ns: 'tickets' })}
                </label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  maxLength={100}
                  value={ticketMetaDraft.plateNumber}
                  onChange={(event) =>
                    setTicketMetaDraft((prev) => ({ ...prev, plateNumber: event.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">
                  {t('evidence.meta.ticketNumber', { ns: 'tickets' })}
                </label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  maxLength={100}
                  value={ticketMetaDraft.ticketNumber}
                  onChange={(event) =>
                    setTicketMetaDraft((prev) => ({ ...prev, ticketNumber: event.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">
                  {t('evidence.meta.location', { ns: 'tickets' })}
                </label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  maxLength={100}
                  value={ticketMetaDraft.issuingLocation}
                  onChange={(event) =>
                    setTicketMetaDraft((prev) => ({ ...prev, issuingLocation: event.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">
                  {t('evidence.meta.violationTime', { ns: 'tickets' })}
                </label>
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  value={ticketMetaDraft.violationDateTime}
                  onChange={(event) =>
                    setTicketMetaDraft((prev) => ({ ...prev, violationDateTime: event.target.value }))
                  }
                />
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  className="rounded-lg bg-emerald-500 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60"
                  onClick={applyTicketMetaDraft}
                  disabled={savingTicketMeta}
                >
                  {savingTicketMeta ? t('common.saving', { defaultValue: 'Saving...' }) : t('common.save', { defaultValue: 'Save' })}
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-emerald-500 hover:text-emerald-600 disabled:opacity-60"
                  onClick={() => setEditingTicketMeta(false)}
                  disabled={savingTicketMeta}
                >
                  {t('common.cancel', { defaultValue: 'Cancel' })}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block pl-2">
          <div className="relative ml-3 space-y-8 border-l-2 border-slate-200 py-2">
            <div className="relative cursor-pointer pl-8">
              <div className="absolute -left-[9px] top-1 h-4 w-4 rounded-full border-2 border-white bg-emerald-500 shadow-sm" />
              <h4 className="text-sm font-semibold text-slate-800">
                {getLocalizedText('Initial Assessment', '初步评估')}
              </h4>
              <p className="mt-0.5 text-xs text-slate-500">{appealPathSummary}</p>
            </div>
            <div className="relative pl-8">
              <div className="absolute -left-[9px] top-1 h-4 w-4 rounded-full border-4 border-emerald-500 bg-white shadow-[0_0_15px_rgba(16,185,129,0.3)]" />
              <h4 className="text-sm font-semibold text-emerald-600">
                {getLocalizedText('Evidence Collection', '证据收集')}
              </h4>
              <p className="mt-0.5 text-xs text-slate-500">
                {getLocalizedText('Gathering context', '补充细节')}
              </p>
            </div>
            <div className="relative pl-8 opacity-50">
              <div className="absolute -left-[9px] top-1 h-4 w-4 rounded-full border-2 border-white bg-slate-200" />
              <h4 className="text-sm font-semibold text-slate-800">
                {getLocalizedText('Generate Appeal', '生成申诉信')}
              </h4>
              <p className="mt-0.5 text-xs text-slate-500">
                {getLocalizedText('Final review', '最终确认')}
              </p>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex flex-1 flex-col gap-6">
        <header>
          <h1 className="text-3xl font-bold text-slate-900">
            {getLocalizedText('Evidence Q&A', '证据问答')}
          </h1>
          <p className="mt-2 text-lg text-slate-500">
            {getLocalizedText(
              'Help our AI understand the context to build your strongest defense.',
              '补充现场信息和凭证，帮助生成更有力的申诉信。'
            )}
          </p>
        </header>

        {loading ? (
          <div className="py-6 text-sm text-slate-500">{t('evidence.form.loading', { ns: 'tickets' })}</div>
        ) : (
          <>
            <VerticalStepperWithAI
              steps={stepItems}
              activeKey={activeStepKey}
              onPrimaryAction={aiRecsEnabled ? handlePrimaryAction : undefined}
              onSecondaryAction={aiRecsEnabled ? handleSecondaryAction : undefined}
              onRedirectAction={aiRecsEnabled && !pathRedirectUsed ? handleRedirectAction : undefined}
            />

            {evidenceItems.length > 0 && (
              <div className="space-y-3">
                {evidenceItems.map((item) => (
                  <EvidenceCard
                    key={item.id}
                    categoryLabel={
                      item.category === 'PAYMENT'
                        ? getLocalizedText('Payment Receipt', '付款凭证')
                        : getLocalizedText('Signage Photo', '标志照片')
                    }
                    filename={item.filename}
                    status={item.status}
                    statusLabel={
                      item.status === 'UPLOADING'
                        ? getLocalizedText('Uploading', '上传中')
                        : item.status === 'UPLOADED'
                        ? getLocalizedText('Uploaded', '已上传')
                        : item.status === 'PROCESSING'
                        ? getLocalizedText('Processing', '处理中')
                        : item.status === 'FAILED'
                        ? getLocalizedText('Failed', '失败')
                        : getLocalizedText('Done', '完成')
                    }
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GuidedEvidenceForm;
